import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { inject, injectable } from 'inversify';
import 'reflect-metadata';

import {
  TYPES as ApiGatewayTypes,
  BatchPayload,
  BatchResponse,
  IApiGateway,
} from '@desygner/ui-core-api-gateway';

import { FluerUserInfo } from './collaborate.fluer-types';
import { ICollaborateService } from './collaborate.interfaces';
import { Collaborator, InvitationStatus, Project, UserId, UserInfo } from './collaborate.types';
import { CollaborateService as BaseCollaborateService } from './collaborate.webrand-service';

dayjs.extend(utc);
const FLUER_DOMAIN = 'fluer';

@injectable()
export class CollaborateService extends BaseCollaborateService implements ICollaborateService {
  constructor(
    @inject(ApiGatewayTypes.ApiGateway)
    protected override apiGateway: IApiGateway
  ) {
    super(apiGateway);
  }

  private get isWorkspace() {
    return this.apiGateway.company !== FLUER_DOMAIN;
  }

  private get fluerEndpointBase() {
    if (this.isWorkspace) {
      return `team/${this.apiGateway.company}/memberships`;
    }

    return `team/memberships`;
  }

  async getMemberships(): Promise<FluerUserInfo[]> {
    if (this.cache.has('memberships')) {
      return this.cache.get('memberships');
    }

    const { data: response } = await this.apiGateway
      .use('fluer')
      .fetch('GET', this.fluerEndpointBase);
    const memberships = response.data;

    this.cache.set('memberships', memberships);
    return memberships;
  }

  override async getMembers({ encodedId, projectId }: Project): Promise<Collaborator[]> {
    const cacheKey = `members-${encodedId}-${projectId}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    const [acceptedMembers, memberships] = await Promise.all([
      super.getMembers({ encodedId, projectId }),
      this.getMemberships(),
    ]);

    const pendingMembers = memberships
      .filter(
        (member) =>
          !!member.extra_data?.project_id?.find((id) => id === encodedId) &&
          (member.status === InvitationStatus.SENT || member.status === InvitationStatus.EXPIRED)
      )
      .map<Collaborator>((member) => ({
        name: member.user.email ?? '',
        email: member.user.email ?? '',
        invitation: {
          membershipId: member.id,
          isPending: true,
          isExpired: member.is_invitation_expired,
          invitedAt: member.invited_at,
        },
        sourceUrl: '',
        permissionId: member.id,
        permissionType: 'shared',
        id: member.user.email ?? '',
      }));

    const invitedMembers = [...acceptedMembers, ...pendingMembers].sort((a, b) =>
      a.email.localeCompare(b.email)
    );

    this.cache.set(cacheKey, invitedMembers);

    return invitedMembers;
  }

  override async addMembers(project: Project, users: UserId[]): Promise<BatchResponse[]> {
    const memberships = await this.getMemberships();
    const nonMembershipPayload = users
      .map<BatchPayload | undefined>((email, index) => {
        const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.toString());

        if (!isEmailValid) return;

        const membership = memberships?.find((member) => member.user.email === email);

        if (membership) {
          return;
        }

        const payload = {
          email: email,
          extra_data: { project_id: [project.encodedId] },
        };

        return {
          id: index,
          body: JSON.stringify(payload),
          method: 'post',
          url: this.fluerEndpointBase,
        };
      })
      .filter((payload): payload is BatchPayload => !!payload);

    const pendingMembership = users
      .map<BatchPayload | undefined>((id, index) => {
        const membership = memberships?.find((member) => member.user.email === id);

        if (!membership) {
          return undefined;
        }

        if (membership.status === InvitationStatus.ACCEPTED) {
          return undefined;
        }

        const now = dayjs(new Date()).utc().format('YYYY-MM-DD HH:mm:ss');

        const payload = {
          invited_at: now,
          extra_data: {
            project_id: [project.encodedId],
          },
        };

        return {
          id: index,
          body: JSON.stringify(payload),
          method: 'put',
          url: `${this.fluerEndpointBase}/${membership.id}`,
        };
      })
      .filter((payload): payload is BatchPayload => !!payload);

    const acceptedMembership = users
      .map((id) => {
        const membership = memberships?.find((member) => member.user.id === id);

        if (!membership || membership.status !== InvitationStatus.ACCEPTED) return;

        return membership.user.id;
      })
      .filter((id): id is string => !!id);

    const requests: Promise<BatchResponse[]>[] = [];

    if (nonMembershipPayload.length !== 0 || pendingMembership.length !== 0) {
      requests.push(
        this.apiGateway.use('fluer').batch([...nonMembershipPayload, ...pendingMembership])
      );
    }

    if (acceptedMembership.length !== 0) {
      requests.push(super.addMembers(project, acceptedMembership));
    }

    if (requests.length === 0) {
      return [];
    }

    const result = await Promise.all(requests);

    this.cache.clear();
    return result.flat();
  }

  override async searchMembers(
    keyword: string,
    status: number,
    limit: number,
    offset: number
  ): Promise<UserInfo[]> {
    const memberships = await this.getMemberships();
    const matchedMemberships =
      memberships.filter((member) => {
        const keywordMatch =
          keyword === '' ||
          member.user.email.toLowerCase().includes(keyword.toLowerCase()) ||
          (member.user.name && member.user.name?.toLowerCase().includes(keyword.toLowerCase()));

        const workspaceMatch = !this.isWorkspace || member.status === InvitationStatus.ACCEPTED;

        return keywordMatch && workspaceMatch;
      }) ?? [];

    return matchedMemberships.slice(offset, limit).map<UserInfo>((member) => ({
      id: member.id,
      user: {
        id: member.id,
        uuid: member.user.id ?? member.user.email,
        nickname: member.user.name ?? member.user.email,
        profilePicture: '',
        details: [{ content: member.user.email, type: 'email' }],
      },
      invitation: {
        membershipId: member.id,
        isPending: member.status === InvitationStatus.SENT,
        isExpired: member.is_invitation_expired,
        invitedAt: member.invited_at,
      },
      expire_date: '',
      invite_date: member.invited_at,
      details: [{ content: member.user.email, type: 'email' }],
      is_expired: member.is_invitation_expired,
    }));
  }

  async resendInvitation({
    project,
    membershipId,
  }: {
    project: Project;
    membershipId: number;
  }): Promise<void> {
    if (!project || !membershipId) {
      return;
    }

    const now = dayjs(new Date()).utc().format('YYYY-MM-DD HH:mm:ss');

    await this.apiGateway.use('fluer').fetch('PUT', `${this.fluerEndpointBase}/${membershipId}`, {
      invited_at: now,
      extra_data: {
        project_id: [project.encodedId],
      },
    });
  }

  async getMembershipById(id: FluerUserInfo['user']['id']): Promise<FluerUserInfo | null> {
    const memberships = await this.getMemberships();

    return memberships.find((member) => member.user.id === id) ?? null;
  }

  async removeWorkspaceMember(collaborator: Collaborator): Promise<void> {
    const membership = await this.getMembershipById(collaborator.id as string);

    if (!membership?.id) {
      return;
    }

    await this.apiGateway
      .use('fluer')
      .fetch('DELETE', `${this.fluerEndpointBase}/${membership.id}`, {});

    this.cache.clear();
    return;
  }

  /**
   * If User is registered, It uses Desygner API
   * If User is not registered, It uses Fluer API
   */
  override async removeMembers(project: Project, users: Collaborator[]): Promise<void> {
    if (users.length === 0) return;

    const pendingUsers = users.filter(
      (user) => user.invitation?.isPending || user.invitation?.isExpired
    );
    const acceptedUsers = users.filter(
      (user) => !user.invitation?.isPending && !user.invitation?.isExpired
    );
    const removeInvites = pendingUsers.map((user) =>
      this.apiGateway
        .use('fluer')
        .fetch(
          'DELETE',
          `${this.fluerEndpointBase}/${user.invitation?.membershipId}/collaboration`,
          { project_id: project.encodedId }
        )
    );

    await Promise.all([super.removeMembers(project, acceptedUsers), ...removeInvites]);

    this.cache.clear();
  }
}
